import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import {
  Briefcase, Check, X, Clock, Send, Wifi, ShieldCheck, ArrowLeft,
  AlertCircle, Zap, Usb, Megaphone, Video, TrendingUp, Headphones, Wrench,
  FlaskConical, Gamepad2, Cpu, Mic, ChevronDown
} from 'lucide-react';
import VoiceRecorder from './VoiceRecorder';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AnimatedSelect — custom dropdown with smooth open/close, animated chevron
 * and staggered options. Used for the Content Creator role to replace the
 * native <select> which can't be animated (OS-controlled).
 */
const AnimatedSelect = ({ value, onChange, options, placeholder, accent = 'purple', testid }) => {
  const [open, setOpen] = React.useState(false);
  const wrapperRef = React.useRef(null);

  React.useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const accentColors = {
    purple: { border: 'border-purple-500/60', bg: 'bg-purple-500/10', text: 'text-purple-200', ring: 'ring-purple-500/30' },
    cyan:   { border: 'border-cyan-500/60',   bg: 'bg-cyan-500/10',   text: 'text-cyan-200',   ring: 'ring-cyan-500/30' },
  }[accent];

  const selected = options.find(o => o.value === value);

  return (
    <div ref={wrapperRef} className="relative" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between bg-black/40 border ${open ? accentColors.border + ' bg-black/60 ring-2 ' + accentColors.ring : 'border-white/10'} rounded-xl px-4 py-3 text-left transition-all duration-300 group`}
      >
        <span className={selected ? 'text-white' : 'text-gray-600'}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${open ? 'rotate-180 ' + accentColors.text : 'group-hover:text-white'}`}
        />
      </button>

      {/* Dropdown */}
      <div
        className={`absolute z-50 left-0 right-0 mt-2 origin-top transition-all duration-200 ease-out ${open ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto' : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'}`}
      >
        <div className={`bg-[#0d0f1e]/95 backdrop-blur-md border ${accentColors.border} rounded-xl shadow-[0_20px_50px_-10px_rgba(0,0,0,0.7)] overflow-hidden`}>
          <ul className="py-1.5 max-h-72 overflow-y-auto">
            {options.map((opt, idx) => {
              const isActive = opt.value === value;
              return (
                <li key={opt.value} style={{ transitionDelay: open ? `${idx * 25}ms` : '0ms' }}
                    className={`transition-all duration-300 ${open ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'}`}>
                  <button
                    type="button"
                    onClick={() => { onChange(opt.value); setOpen(false); }}
                    className={`w-full px-4 py-2.5 text-left text-sm flex items-center justify-between gap-3 transition-colors ${
                      isActive
                        ? `${accentColors.bg} ${accentColors.text} font-medium`
                        : 'text-gray-300 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <span>{opt.label}</span>
                    {isActive && <Check className={`w-4 h-4 ${accentColors.text} flex-shrink-0`} />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};

const RecruitmentPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, token, user, login } = useAuth();

  const [step, setStep] = useState('choose'); // 'choose' | 'form'
  const [roleType, setRoleType] = useState(null); // 'helper' | 'community_manager' | 'tester' | 'content_creator'

  const [recruitmentSettings, setRecruitmentSettings] = useState({
    helper: { open: true, visible: true },
    community_manager: { open: true, visible: true },
    tester: { open: true, visible: true },
    content_creator: { open: true, visible: true },
  });

  useEffect(() => {
    fetch(`${API_URL}/api/recruitment-settings`)
      .then(r => r.json())
      .then(setRecruitmentSettings)
      .catch(() => {});
  }, []);

  const [existing, setExisting] = useState(null);
  const [loadingExisting, setLoadingExisting] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [form, setForm] = useState({
    age: '',
    has_windows_install: false,
    it_skills: '',
    availability: '',
    experience: '',
    motivation: '',
    value_proposition: '',
    social_handles: '',
    contact_discord: '',
    games_played: '',
    pc_specs: '',
    can_reinstall_windows: false,
    creator_platform: '',
    creator_channel_url: '',
    creator_audience_size: '',
    creator_content_type: '',
  });
  const [voiceMessage, setVoiceMessage] = useState(null); // { base64, mime, duration } | null

  const fetchMyApplication = useCallback(async () => {
    if (!isAuthenticated || !token) {
      setLoadingExisting(false);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/helper-applications/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setExisting(data.application);
      }
    } catch (e) { /* silent */ }
    finally { setLoadingExisting(false); }
  }, [isAuthenticated, token]);

  useEffect(() => { fetchMyApplication(); }, [fetchMyApplication]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const pickRole = (role) => {
    const cfg = recruitmentSettings[role];
    if (cfg && cfg.open === false) {
      setError('Les candidatures pour ce poste sont temporairement fermées.');
      return;
    }
    setRoleType(role);
    setStep('form');
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isAuthenticated) { login(); return; }

    const ageNum = parseInt(form.age, 10);
    if (Number.isNaN(ageNum) || ageNum < 13 || ageNum > 99) {
      setError('Âge invalide (13–99).'); return;
    }
    if (roleType === 'helper' && !form.has_windows_install) {
      setError('Tu dois savoir installer Windows correctement (Rufus, drivers Wi-Fi/Ethernet) pour postuler Helper.');
      return;
    }
    if (roleType === 'helper' && !form.it_skills.trim()) {
      setError('Détaille tes compétences en informatique.'); return;
    }
    if (roleType === 'community_manager' && !form.social_handles.trim()) {
      setError('Indique tes réseaux sociaux (TikTok, Instagram, etc.).'); return;
    }
    if (roleType === 'tester' && !form.can_reinstall_windows) {
      setError('Tu dois être autonome pour réinstaller Windows en cas de besoin.'); return;
    }
    if (roleType === 'tester' && !form.games_played.trim()) {
      setError('Indique au moins les jeux auxquels tu joues.'); return;
    }
    if (roleType === 'content_creator' && !form.creator_platform) {
      setError('Choisis ta plateforme principale.'); return;
    }
    if (roleType === 'content_creator' && !form.creator_channel_url.trim()) {
      setError('Ajoute le lien de ta chaîne / profil principal.'); return;
    }
    if (roleType === 'content_creator' && !form.creator_audience_size) {
      setError('Sélectionne la taille approximative de ton audience.'); return;
    }
    // Creator skips availability + experience (those fields are hidden for that role)
    const requireAvailExp = roleType !== 'content_creator';
    if (
      (requireAvailExp && (!form.availability.trim() || !form.experience.trim())) ||
      !form.motivation.trim() ||
      !form.value_proposition.trim()
    ) {
      setError('Merci de remplir tous les champs.'); return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/helper-applications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          role_type: roleType,
          age: ageNum,
          has_windows_install: form.has_windows_install,
          it_skills: form.it_skills,
          availability: form.availability,
          experience: form.experience,
          motivation: form.motivation,
          value_proposition: form.value_proposition,
          social_handles: form.social_handles,
          contact_discord: form.contact_discord,
          games_played: form.games_played,
          pc_specs: form.pc_specs,
          can_reinstall_windows: form.can_reinstall_windows,
          creator_platform: form.creator_platform,
          creator_channel_url: form.creator_channel_url,
          creator_audience_size: form.creator_audience_size,
          creator_content_type: form.creator_content_type,
          voice_message_b64: voiceMessage ? voiceMessage.base64 : null,
          voice_message_mime: voiceMessage ? voiceMessage.mime : null,
          voice_message_duration: voiceMessage ? voiceMessage.duration : null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || 'Erreur lors de l\'envoi.');
      } else {
        setSuccess(true);
        setExisting(data.application);
      }
    } catch (err) {
      setError('Erreur réseau.');
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = (status) => {
    if (status === 'approved') {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-green-500/15 text-green-400 border border-green-500/30">
          <Check className="w-4 h-4" /> Acceptée
        </span>
      );
    }
    if (status === 'rejected') {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-red-500/15 text-red-400 border border-red-500/30">
          <X className="w-4 h-4" /> Refusée
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-yellow-500/15 text-yellow-400 border border-yellow-500/30">
        <Clock className="w-4 h-4" /> En attente
      </span>
    );
  };

  const isHelper = roleType === 'helper';
  const isCM = roleType === 'community_manager';
  const isTester = roleType === 'tester';
  const isCreator = roleType === 'content_creator';

  const roleLabel = isCreator ? 'Content Creator'
    : isCM ? 'Community Manager'
    : isTester ? 'Bêta-Testeur'
    : 'Helper Discord';

  return (
    <div className="relative min-h-screen py-8 md:py-16 px-4 md:px-8 overflow-hidden">
      {/* Ambient background glow */}
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[680px] h-[680px] bg-cyan-500/10 rounded-full blur-3xl"></div>
      <div className="pointer-events-none absolute top-40 right-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>

      <div className="relative max-w-4xl mx-auto">
        <button
          data-testid="recruitment-back-btn"
          onClick={() => step === 'form' ? setStep('choose') : navigate(-1)}
          className="group flex items-center gap-2 text-cyan-400 hover:text-cyan-300 mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          {step === 'form' ? 'Retour aux postes' : 'Retour'}
        </button>

        {/* Header */}
        <div className="text-center mb-12 animate-fadeInUp">
          <div className="mb-5">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-[10px] md:text-xs font-semibold uppercase tracking-[0.18em]">
              <Zap className="w-3 h-3" /> Recrutement ouvert
            </span>
          </div>
          <div className="relative inline-block mb-5">
            <div className="absolute inset-0 bg-cyan-500/30 blur-2xl rounded-full"></div>
            <div className="relative w-20 h-20 bg-gradient-to-br from-cyan-500/20 to-blue-500/10 border border-cyan-500/30 rounded-2xl flex items-center justify-center backdrop-blur-sm">
              <Briefcase className="w-10 h-10 text-cyan-300" />
            </div>
          </div>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-3 tracking-tight">
            Rejoins l'<span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">équipe AqmOpti</span>
          </h1>
          <p className="text-gray-400 max-w-xl mx-auto text-sm md:text-base">
            On recrute des passionnés pour aider la communauté et faire grandir AqmOpti.
            Choisis le poste qui te correspond.
          </p>
        </div>

        {/* === NOT CONNECTED — Show before role cards === */}
        {!isAuthenticated && !loadingExisting && (
          <div className="mb-6 bg-[#1e1f2e]/80 border border-indigo-500/25 rounded-2xl p-6 md:p-8 text-center backdrop-blur-sm animate-fadeInUp" data-testid="recruitment-login-prompt">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-indigo-500/20 border border-indigo-500/30 mb-4">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" className="text-indigo-300">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
              </svg>
            </div>
            <h3 className="text-lg md:text-xl font-bold text-white mb-2">Connecte-toi avec Discord pour postuler</h3>
            <p className="text-sm text-gray-400 mb-5 max-w-md mx-auto">
              On a besoin de ton compte Discord pour traiter ta candidature et te contacter.
            </p>
            <button
              data-testid="recruitment-login-btn"
              onClick={login}
              className="inline-flex items-center gap-2.5 px-6 py-3 bg-[#5865F2] hover:bg-[#4752C4] text-white font-semibold rounded-xl transition-all duration-200 shadow-[0_4px_15px_rgba(88,101,242,0.3)]"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
              </svg>
              Se connecter avec Discord
            </button>
          </div>
        )}

        {/* === STEP: CHOOSE ROLE === */}
        {step === 'choose' && !success && !(existing && existing.status !== 'rejected') && (
          <>
          {!recruitmentSettings.helper.visible && !recruitmentSettings.community_manager.visible && !recruitmentSettings.tester.visible && !recruitmentSettings.content_creator?.visible && (
            <div className="text-center py-16 max-w-md mx-auto" data-testid="recruitment-closed-banner">
              <div className="w-14 h-14 rounded-2xl bg-zinc-900 border border-zinc-800 flex items-center justify-center mx-auto mb-5">
                <Clock className="w-6 h-6 text-zinc-500" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2 tracking-tight">Recrutement fermé</h3>
              <p className="text-zinc-400 text-sm">Reviens plus tard, on ouvrira de nouveau les candidatures bientôt.</p>
            </div>
          )}
          {(() => {
            const visibleCount = [
              recruitmentSettings.helper.visible,
              recruitmentSettings.community_manager.visible,
              recruitmentSettings.tester.visible,
              recruitmentSettings.content_creator?.visible,
            ].filter(Boolean).length;
            const gridCls = visibleCount >= 4
              ? 'md:grid-cols-2'
              : visibleCount === 3 ? 'md:grid-cols-2 lg:grid-cols-3'
              : visibleCount === 2 ? 'md:grid-cols-2'
              : 'md:grid-cols-1';
            return (
          <div className={`grid grid-cols-1 ${gridCls} gap-6 md:gap-8 max-w-5xl mx-auto animate-fadeInUp`} style={{ animationDelay: '120ms' }}>

            {/* Helper card */}
            {recruitmentSettings.helper.visible && (
            <button
              data-testid="role-helper-btn"
              onClick={() => isAuthenticated ? pickRole('helper') : login()}
              disabled={!recruitmentSettings.helper.open}
              className={`group text-left relative bg-gradient-to-b from-zinc-900/70 to-zinc-950/70 border rounded-2xl p-6 backdrop-blur-sm transition-all duration-300 ${
                recruitmentSettings.helper.open
                  ? 'border-white/10 hover:border-cyan-500/50 hover:-translate-y-1 hover:shadow-[0_0_40px_-8px_rgba(6,182,212,0.5)]'
                  : 'border-zinc-800/60 opacity-60 cursor-not-allowed'
              }`}
            >
              {/* Top badge row — reserves space so it never overlaps the title */}
              <div className="h-6 mb-3 flex items-start">
                {!recruitmentSettings.helper.open && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-800 border border-zinc-700 text-zinc-400 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap">Fermé</span>
                )}
              </div>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center group-hover:bg-cyan-500/20 transition-colors">
                  <Headphones className="w-6 h-6 text-cyan-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold text-white tracking-tight">Helper Discord</h3>
                  <p className="text-xs text-cyan-300 uppercase tracking-wider mt-0.5">Support technique</p>
                </div>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed mb-5">
                Aide la communauté sur Discord : installation Windows, drivers, dépannage simple.
                Tu es le premier contact des clients.
              </p>
              <div className="space-y-1.5 mb-5">
                {['Installer Windows (Rufus, drivers Wi-Fi/Ethernet)', 'Aider sur les tickets Discord', 'Bases solides en info'].map((it, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-gray-300">
                    <Check className="w-3.5 h-3.5 text-cyan-400 mt-0.5 flex-shrink-0" />
                    {it}
                  </div>
                ))}
              </div>
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-300 group-hover:text-cyan-200">
                {!recruitmentSettings.helper.open
                  ? 'Candidatures fermées'
                  : (isAuthenticated ? 'Postuler Helper' : 'Connecte-toi pour postuler')}
                <ArrowLeft className="w-3.5 h-3.5 rotate-180 group-hover:translate-x-0.5 transition-transform" />
              </span>
            </button>
            )}

            {/* Community Manager card */}
            {recruitmentSettings.community_manager.visible && (
            <button
              data-testid="role-cm-btn"
              onClick={() => isAuthenticated ? pickRole('community_manager') : login()}
              disabled={!recruitmentSettings.community_manager.open}
              className={`group text-left relative bg-gradient-to-b from-zinc-900/70 to-zinc-950/70 border rounded-2xl p-6 backdrop-blur-sm transition-all duration-300 ${
                recruitmentSettings.community_manager.open
                  ? 'border-white/10 hover:border-pink-500/50 hover:-translate-y-1 hover:shadow-[0_0_40px_-8px_rgba(236,72,153,0.4)]'
                  : 'border-zinc-800/60 opacity-60 cursor-not-allowed'
              }`}
            >
              {/* Top badge row — reserves space so it never overlaps the title */}
              <div className="h-6 mb-3 flex items-start">
                {!recruitmentSettings.community_manager.open && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-800 border border-zinc-700 text-zinc-400 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap">Fermé</span>
                )}
              </div>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-pink-500/10 border border-pink-500/30 flex items-center justify-center group-hover:bg-pink-500/20 transition-colors">
                  <Megaphone className="w-6 h-6 text-pink-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold text-white tracking-tight">Community Manager</h3>
                  <p className="text-xs text-pink-300 uppercase tracking-wider mt-0.5">Réseaux sociaux</p>
                </div>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed mb-5">
                Gère et fais grandir notre présence sur TikTok, Instagram, X.
                Crée du contenu, anime la communauté, attire de nouveaux clients.
              </p>
              <div className="space-y-1.5 mb-5">
                {['Création de contenu (TikTok, Reels, Shorts)', 'Gestion des réseaux & engagement', 'Idées créatives & sens du gaming'].map((it, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-gray-300">
                    <Check className="w-3.5 h-3.5 text-pink-400 mt-0.5 flex-shrink-0" />
                    {it}
                  </div>
                ))}
              </div>
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-pink-300 group-hover:text-pink-200">
                {!recruitmentSettings.community_manager.open
                  ? 'Candidatures fermées'
                  : (isAuthenticated ? 'Postuler Community Manager' : 'Connecte-toi pour postuler')}
                <ArrowLeft className="w-3.5 h-3.5 rotate-180 group-hover:translate-x-0.5 transition-transform" />
              </span>
            </button>
            )}

            {/* Tester card */}
            {recruitmentSettings.tester.visible && (
            <button
              data-testid="role-tester-btn"
              onClick={() => isAuthenticated ? pickRole('tester') : login()}
              disabled={!recruitmentSettings.tester.open}
              className={`group text-left relative bg-gradient-to-b from-zinc-900/70 to-zinc-950/70 border rounded-2xl p-6 backdrop-blur-sm transition-all duration-300 ${
                recruitmentSettings.tester.open
                  ? 'border-white/10 hover:border-amber-500/50 hover:-translate-y-1 hover:shadow-[0_0_40px_-8px_rgba(251,191,36,0.4)]'
                  : 'border-zinc-800/60 opacity-60 cursor-not-allowed'
              }`}
            >
              {/* Top badge row — reserves space so it never overlaps the title */}
              <div className="h-6 mb-3 flex items-start">
                {!recruitmentSettings.tester.open ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-800 border border-zinc-700 text-zinc-400 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap">Fermé</span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap">
                    Accès anticipé
                  </span>
                )}
              </div>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center group-hover:bg-amber-500/20 transition-colors">
                  <FlaskConical className="w-6 h-6 text-amber-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold text-white tracking-tight">Bêta-Testeur</h3>
                  <p className="text-xs text-amber-300 uppercase tracking-wider mt-0.5">Optimisations en avant-première</p>
                </div>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed mb-5">
                Teste les nouvelles optimisations <strong className="text-gray-200">avant leur sortie publique</strong>.
                Tu donnes ton retour, on ajuste, et tu gardes une longueur d'avance en jeu.
              </p>
              <div className="space-y-1.5 mb-5">
                {['Autonome pour réinstaller Windows', 'PC gaming actif (3+ jeux réguliers)', 'Capable de faire des retours clairs'].map((it, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-gray-300">
                    <Check className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                    {it}
                  </div>
                ))}
              </div>
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-300 group-hover:text-amber-200">
                {!recruitmentSettings.tester.open
                  ? 'Candidatures fermées'
                  : (isAuthenticated ? 'Postuler Bêta-Testeur' : 'Connecte-toi pour postuler')}
                <ArrowLeft className="w-3.5 h-3.5 rotate-180 group-hover:translate-x-0.5 transition-transform" />
              </span>
            </button>
            )}

            {/* Content Creator card */}
            {recruitmentSettings.content_creator?.visible && (
            <button
              data-testid="role-creator-btn"
              onClick={() => isAuthenticated ? pickRole('content_creator') : login()}
              disabled={!recruitmentSettings.content_creator?.open}
              className={`group text-left relative bg-gradient-to-b from-zinc-900/70 to-zinc-950/70 border rounded-2xl p-6 backdrop-blur-sm transition-all duration-300 ${
                recruitmentSettings.content_creator?.open
                  ? 'border-white/10 hover:border-purple-500/50 hover:-translate-y-1 hover:shadow-[0_0_40px_-8px_rgba(168,85,247,0.5)]'
                  : 'border-zinc-800/60 opacity-60 cursor-not-allowed'
              }`}
            >
              {/* Top badge row — reserves space so it never overlaps the title */}
              <div className="h-6 mb-3 flex items-start">
                {!recruitmentSettings.content_creator?.open ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-800 border border-zinc-700 text-zinc-400 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap">Fermé</span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-500/15 border border-purple-500/30 text-purple-300 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap">
                    Partenariat
                  </span>
                )}
              </div>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center group-hover:bg-purple-500/20 transition-colors">
                  <Video className="w-6 h-6 text-purple-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold text-white tracking-tight">Content Creator</h3>
                  <p className="text-xs text-purple-300 uppercase tracking-wider mt-0.5">Streamers · YouTubers · TikTok</p>
                </div>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed mb-5">
                Tu as une communauté gaming ? Parle d'AqmOpti à ton audience et touche une <strong className="text-purple-200">commission sur chaque vente</strong>.
                Plus tu performes, plus ton taux grimpe.
              </p>
              <div className="space-y-1.5 mb-5">
                {['Lien affilié personnalisé', 'Commission progressive jusqu\'à 50%', 'Suivi des gains en temps réel'].map((it, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-gray-300">
                    <Check className="w-3.5 h-3.5 text-purple-400 mt-0.5 flex-shrink-0" />
                    {it}
                  </div>
                ))}
              </div>
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-purple-300 group-hover:text-purple-200">
                {!recruitmentSettings.content_creator?.open
                  ? 'Candidatures fermées'
                  : (isAuthenticated ? 'Postuler Creator' : 'Connecte-toi pour postuler')}
                <ArrowLeft className="w-3.5 h-3.5 rotate-180 group-hover:translate-x-0.5 transition-transform" />
              </span>
            </button>
            )}
          </div>
          );
          })()}
          </>
        )}

        {/* Already applied */}
        {isAuthenticated && existing && !success && existing.status !== 'rejected' && (
          <div data-testid="recruitment-existing-status" className="bg-gradient-to-b from-zinc-900/70 to-zinc-950/70 border border-white/10 rounded-2xl p-6 md:p-8 mb-6 backdrop-blur-sm">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-white tracking-tight">Ta candidature</h2>
                <p className="text-xs text-gray-500 mt-1 uppercase tracking-wider">
                  {existing.role_type === 'community_manager'
                    ? 'Community Manager'
                    : existing.role_type === 'tester'
                      ? 'Bêta-Testeur'
                      : existing.role_type === 'content_creator'
                        ? 'Content Creator'
                        : 'Helper Discord'}
                </p>
              </div>
              {statusBadge(existing.status)}
            </div>
            <p className="text-gray-400 text-sm mb-2">
              Envoyée le {new Date(existing.created_at).toLocaleString('fr-FR')}
            </p>
            {existing.admin_note && (
              <div className="mt-4 p-4 bg-black/40 border border-white/10 rounded-xl">
                <p className="text-xs uppercase text-gray-500 mb-1 tracking-wider">Note du staff</p>
                <p className="text-gray-300">{existing.admin_note}</p>
              </div>
            )}
          </div>
        )}

        {/* Success */}
        {success && (
          <div className="bg-gradient-to-b from-green-500/15 to-green-500/5 border border-green-500/30 rounded-2xl p-8 text-center backdrop-blur-sm" data-testid="recruitment-success">
            <div className="w-14 h-14 rounded-2xl bg-green-500/20 border border-green-500/30 flex items-center justify-center mx-auto mb-4">
              <Check className="w-7 h-7 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2 tracking-tight">Candidature envoyée !</h2>
            <p className="text-gray-300">Le staff va l'examiner. Reviens ici pour suivre le statut.</p>
          </div>
        )}

        {/* === STEP: FORM === */}
        {step === 'form' && isAuthenticated && (!existing || existing.status === 'rejected') && !success && (
          <form
            onSubmit={handleSubmit}
            className="relative bg-gradient-to-b from-zinc-900/70 to-zinc-950/80 border border-white/10 rounded-2xl p-6 md:p-8 space-y-6 backdrop-blur-sm animate-fadeInUp"
            data-testid="recruitment-form"
          >
            {/* Role banner */}
            <div className={`flex items-center gap-3 p-4 rounded-xl border ${
              isCM ? 'bg-pink-500/10 border-pink-500/30' :
              isTester ? 'bg-amber-500/10 border-amber-500/30' :
              'bg-cyan-500/10 border-cyan-500/30'
            }`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                isCM ? 'bg-pink-500/20' :
                isTester ? 'bg-amber-500/20' :
                'bg-cyan-500/20'
              }`}>
                {isCM ? <Megaphone className="w-5 h-5 text-pink-300" /> :
                 isTester ? <FlaskConical className="w-5 h-5 text-amber-300" /> :
                 <Headphones className="w-5 h-5 text-cyan-300" />}
              </div>
              <div className="flex-1">
                <p className="text-xs uppercase tracking-wider text-gray-400">Tu postules pour</p>
                <p className="text-white font-semibold">{roleLabel}</p>
              </div>
            </div>

            {/* User header */}
            <div className="flex items-center gap-3 pb-5 border-b border-white/10">
              {user?.avatar && (
                <img
                  src={`https://cdn.discordapp.com/avatars/${user.discord_id}/${user.avatar}.png`}
                  alt=""
                  className="w-11 h-11 rounded-full ring-2 ring-cyan-500/30"
                />
              )}
              <div>
                <p className="text-white font-medium">{user?.username}</p>
                <p className="text-xs text-gray-500">Tu postules avec ton compte Discord</p>
              </div>
            </div>

            {/* Age + Discord */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wider">Âge *</label>
                <input
                  type="number" name="age" min="13" max="99" required
                  value={form.age} onChange={handleChange}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-cyan-500/60 focus:bg-black/60 transition-all"
                  data-testid="recruitment-age-input"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wider">Pseudo Discord (autre que celui connecté ?)</label>
                <input
                  type="text" name="contact_discord"
                  value={form.contact_discord} onChange={handleChange}
                  placeholder={user?.username || ''}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-cyan-500/60 focus:bg-black/60 transition-all"
                  data-testid="recruitment-contact-input"
                />
              </div>
            </div>

            {/* HELPER ONLY — Windows install */}
            {isHelper && (
              <div className={`relative rounded-xl border transition-all ${form.has_windows_install ? 'bg-cyan-500/10 border-cyan-500/40' : 'bg-amber-500/5 border-amber-500/30'}`}>
                <div className="flex items-start gap-3 p-4">
                  <div className={`flex-shrink-0 mt-0.5 ${form.has_windows_install ? 'text-cyan-300' : 'text-amber-400'}`}>
                    {form.has_windows_install ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                  </div>
                  <label className="flex-1 cursor-pointer">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox" name="has_windows_install"
                        checked={form.has_windows_install} onChange={handleChange}
                        className="mt-1 w-5 h-5 accent-cyan-500 flex-shrink-0"
                        data-testid="recruitment-windows-checkbox"
                      />
                      <div>
                        <p className="text-sm font-semibold text-white mb-1">
                          Je sais installer Windows correctement <span className="text-cyan-300">(obligatoire)</span>
                        </p>
                        <p className="text-xs text-gray-400 leading-relaxed">
                          Création d'une clé USB bootable avec <span className="text-gray-200 font-medium">Rufus</span>, installation propre,
                          drivers <span className="text-gray-200 font-medium">Wi-Fi / Ethernet</span> au cas où.
                        </p>
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* HELPER ONLY — IT skills */}
            {isHelper && (
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wider">Compétences en informatique *</label>
                <textarea
                  name="it_skills" required rows={3}
                  value={form.it_skills} onChange={handleChange}
                  placeholder="Drivers Wi-Fi/Ethernet, dépannage Windows, BIOS, montage PC..."
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-cyan-500/60 focus:bg-black/60 transition-all resize-none"
                  data-testid="recruitment-skills-input"
                />
              </div>
            )}

            {/* CM ONLY — Social handles */}
            {isCM && (
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wider">Tes réseaux sociaux *</label>
                <textarea
                  name="social_handles" required rows={3}
                  value={form.social_handles} onChange={handleChange}
                  placeholder="TikTok: @ton_pseudo (12k abonnés)&#10;Instagram: @ton_pseudo&#10;X / YouTube..."
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-pink-500/60 focus:bg-black/60 transition-all resize-none"
                  data-testid="recruitment-socials-input"
                />
                <p className="text-xs text-gray-500 mt-1.5">Inclus tes pseudos + nombre d'abonnés si tu as déjà une audience.</p>
              </div>
            )}

            {/* TESTER ONLY — Reinstall Windows autonomy */}
            {isTester && (
              <div className={`relative rounded-xl border transition-all ${form.can_reinstall_windows ? 'bg-amber-500/10 border-amber-500/40' : 'bg-amber-500/5 border-amber-500/30'}`}>
                <div className="flex items-start gap-3 p-4">
                  <div className={`flex-shrink-0 mt-0.5 ${form.can_reinstall_windows ? 'text-amber-300' : 'text-amber-400'}`}>
                    {form.can_reinstall_windows ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                  </div>
                  <label className="flex-1 cursor-pointer">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox" name="can_reinstall_windows"
                        checked={form.can_reinstall_windows} onChange={handleChange}
                        className="mt-1 w-5 h-5 accent-amber-500 flex-shrink-0"
                        data-testid="recruitment-reinstall-checkbox"
                      />
                      <div>
                        <p className="text-sm font-semibold text-white mb-1">
                          Je suis autonome pour réinstaller Windows <span className="text-amber-300">(obligatoire)</span>
                        </p>
                        <p className="text-xs text-gray-400 leading-relaxed">
                          Les optis en bêta peuvent ponctuellement casser la session — il faut pouvoir formater
                          et réinstaller Windows seul (Rufus, drivers Wi-Fi/Ethernet) sans support.
                        </p>
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* TESTER ONLY — Games played */}
            {isTester && (
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wider flex items-center gap-1.5">
                  <Gamepad2 className="w-3.5 h-3.5 text-amber-300" />
                  Jeux principaux *
                </label>
                <textarea
                  name="games_played" required rows={3}
                  value={form.games_played} onChange={handleChange}
                  placeholder="Ex: Valorant (compétitif), CS2, Fortnite, Apex, Rainbow Six..."
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-amber-500/60 focus:bg-black/60 transition-all resize-none"
                  data-testid="recruitment-games-input"
                />
                <p className="text-xs text-gray-500 mt-1.5">Précise le niveau si compétitif (rang, h/jour).</p>
              </div>
            )}

            {/* TESTER ONLY — PC specs */}
            {isTester && (
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wider flex items-center gap-1.5">
                  <Cpu className="w-3.5 h-3.5 text-amber-300" />
                  Configuration PC
                </label>
                <textarea
                  name="pc_specs" rows={2}
                  value={form.pc_specs} onChange={handleChange}
                  placeholder="Ex: Ryzen 5 5600 / RTX 3060 / 16Go DDR4 / Windows 11"
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-amber-500/60 focus:bg-black/60 transition-all resize-none"
                  data-testid="recruitment-pcspecs-input"
                />
                <p className="text-xs text-gray-500 mt-1.5">CPU / GPU / RAM / OS — utile pour cibler les tests adaptés à ton matériel.</p>
              </div>
            )}

            {/* CREATOR ONLY — Platform / channel / audience / content */}
            {isCreator && (
              <div className="space-y-4 animate-[fadeIn_0.5s_ease-out]">
                <div>
                  <label className="block text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wider">Plateforme principale *</label>
                  <AnimatedSelect
                    value={form.creator_platform}
                    onChange={(v) => setForm(f => ({ ...f, creator_platform: v }))}
                    placeholder="Sélectionner ta plateforme..."
                    accent="purple"
                    testid="recruitment-creator-platform"
                    options={[
                      { value: 'twitch',    label: 'Twitch' },
                      { value: 'youtube',   label: 'YouTube' },
                      { value: 'tiktok',    label: 'TikTok' },
                      { value: 'instagram', label: 'Instagram' },
                      { value: 'x',         label: 'X / Twitter' },
                      { value: 'kick',      label: 'Kick' },
                      { value: 'other',     label: 'Autre' },
                    ]}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wider">Lien de ta chaîne / profil *</label>
                  <input
                    type="url" name="creator_channel_url" required
                    value={form.creator_channel_url} onChange={handleChange}
                    placeholder="https://twitch.tv/ton_pseudo"
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-purple-500/60 focus:bg-black/60 transition-all"
                    data-testid="recruitment-creator-url"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wider">Taille d'audience approximative *</label>
                  <AnimatedSelect
                    value={form.creator_audience_size}
                    onChange={(v) => setForm(f => ({ ...f, creator_audience_size: v }))}
                    placeholder="Sélectionner ta taille d'audience..."
                    accent="purple"
                    testid="recruitment-creator-audience"
                    options={[
                      { value: '<1k',      label: 'Moins de 1 000' },
                      { value: '1k-10k',   label: '1 000 – 10 000' },
                      { value: '10k-50k',  label: '10 000 – 50 000' },
                      { value: '50k-100k', label: '50 000 – 100 000' },
                      { value: '100k+',    label: 'Plus de 100 000' },
                    ]}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wider">Type de contenu</label>
                  <textarea
                    name="creator_content_type" rows={2}
                    value={form.creator_content_type} onChange={handleChange}
                    placeholder="Ex: Stream Valorant/CS2 le soir, vidéos tips PC, shorts gaming..."
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-purple-500/60 focus:bg-black/60 transition-all resize-none"
                    data-testid="recruitment-creator-content"
                  />
                  <p className="text-xs text-gray-500 mt-1.5">Ce que tu poses sur tes réseaux : jeux, format, fréquence.</p>
                </div>
              </div>
            )}

            {/* Experience — hidden for Content Creator */}
            {!isCreator && (
            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wider">Expérience *</label>
              <textarea
                name="experience" required rows={3}
                value={form.experience} onChange={handleChange}
                placeholder={isCM
                  ? "Tes expériences en création de contenu, gestion de communauté, vidéos..."
                  : isTester
                    ? "Combien de jeux/optimisations tu as déjà testés, expérience en feedback, tweaks Windows..."
                    : "Tes expériences (perso, pro, communauté)..."
                }
                className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-cyan-500/60 focus:bg-black/60 transition-all resize-none"
                data-testid="recruitment-experience-input"
              />
            </div>
            )}

            {/* Value proposition — KEY FIELD */}
            <div className="relative">
              <label className="block text-xs font-semibold text-cyan-300 mb-2 uppercase tracking-wider flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5" />
                Qu'est-ce que tu nous apportes ? *
              </label>
              <textarea
                name="value_proposition" required rows={4}
                value={form.value_proposition} onChange={handleChange}
                placeholder={isCM
                  ? "Ex: Je peux poster 3 TikToks/semaine sur des tips gaming, j'ai déjà 8k abonnés sur ma chaîne, j'ai un bon œil pour le montage..."
                  : isTester
                    ? "Ex: Je joue 4h/jour à Valorant niveau Diamant, je fais des retours détaillés avec captures, je peux tester sur 2 PC différents..."
                    : isCreator
                      ? "Ex: J'ai 5k viewers/stream sur Twitch FR, j'ai déjà fait des partenariats efficaces, audience 16-25 ans gaming..."
                      : "Ex: J'ai 5 ans d'expérience en dépannage, je suis dispo 4h/jour, je suis très pédagogue avec les débutants..."
                }
                className="w-full bg-black/40 border border-cyan-500/30 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-cyan-500/60 focus:bg-black/60 transition-all resize-none"
                data-testid="recruitment-value-input"
              />
              <p className="text-xs text-gray-500 mt-1.5">
                Concret : qu'est-ce que tu fais de mieux que les autres ? Pourquoi on devrait te choisir ?
              </p>
            </div>

            {/* Availability — hidden for Content Creator */}
            {!isCreator && (
            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wider">Disponibilités *</label>
              <input
                type="text" name="availability" required
                value={form.availability} onChange={handleChange}
                placeholder="Ex: soirs en semaine, week-ends, ~2h/jour..."
                className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-cyan-500/60 focus:bg-black/60 transition-all"
                data-testid="recruitment-availability-input"
              />
            </div>
            )}

            {/* Motivation */}
            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wider">Motivation *</label>
              <textarea
                name="motivation" required rows={3}
                value={form.motivation} onChange={handleChange}
                placeholder="Pourquoi tu veux rejoindre l'équipe AqmOpti ?"
                className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-cyan-500/60 focus:bg-black/60 transition-all resize-none"
                data-testid="recruitment-motivation-input"
              />
            </div>

            {/* Voice message (optional) */}
            <VoiceRecorder
              maxSeconds={60}
              accent={isCreator ? 'purple' : isCM ? 'pink' : isTester ? 'amber' : 'cyan'}
              onChange={setVoiceMessage}
            />

            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-300 text-sm" data-testid="recruitment-error">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit" disabled={submitting}
              className={`group relative w-full flex items-center justify-center gap-2 px-6 py-3.5 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-all shadow-[0_0_30px_-6px_rgba(6,182,212,0.5)] hover:shadow-[0_0_40px_-4px_rgba(6,182,212,0.7)] ${
                isCreator
                  ? 'bg-gradient-to-r from-purple-500 to-fuchsia-500 hover:from-purple-400 hover:to-fuchsia-400'
                  : isCM
                    ? 'bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-400 hover:to-purple-400'
                    : isTester
                      ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400'
                      : 'bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400'
              }`}
              data-testid="recruitment-submit-btn"
            >
              <Send className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              {submitting ? 'Envoi...' : 'Envoyer ma candidature'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default RecruitmentPage;
